/**
 * /try create-module tab bar.
 *
 * WHAT THIS FILE DOES:
 * - Renders the five-tab signage row above the existing /try input container:
 *   Text · URL · File · YouTube · Google Doc.
 * - Owns active-mode state, click activation, and keyboard navigation.
 * - Persists the active mode in sessionStorage via the consumer's `onChange`
 *   callback (this module does not touch storage itself).
 *
 * WHY THIS EXISTS:
 * - Today's /try hides PDF, audio/video, YouTube, and Google Doc support
 *   behind a single textarea + paperclip. The tab row makes capability
 *   visible without forking the submit pipeline.
 * - Keeping this module small and pure (no DOM lookups outside the provided
 *   container, no submit logic) preserves the canonical contract that
 *   `TryUIController` owns submit.
 *
 * KEYBOARD MODEL:
 * - Roving tabindex: only the active tab is in the tab order.
 * - ArrowLeft / ArrowRight cycle focus across tabs (manual activation).
 * - Home / End jump to first / last tab.
 * - Enter / Space activate the focused tab.
 *
 * NON-GOALS:
 * - Submit orchestration, ingest API calls, validation, or storage I/O.
 * - Per-preset placeholder/helper copy — that lives in
 *   `src/client/try-mode-affordances.ts` (Phase 2).
 *
 * RELATED FILES:
 * - src/client/try.ts mounts this when the UI flag is on.
 * - src/ssr/try.ts renders the `#try-mode-tabs` slot when the flag is on.
 * - src/client/try-ui.ts owns the existing input container and submit.
 * - src/lib/try-content-type-labels.ts (canonical tab labels)
 */

import { TryContentTypeLabel } from '../lib/try-content-type-labels.js';
import { createTryFormatIconElement, resolveTryFormatIconKind } from './try-format-icons.js';

/**
 * Stable identifiers for the five create-module presets.
 *
 * Exposed as a const-record so consumers (try-mode-affordances.ts, tests,
 * try-ui.ts preset API) can reference modes by name rather than by string
 * literals scattered across modules.
 */
export const TryModes = Object.freeze({
  TEXT: 'text',
  URL: 'url',
  FILE: 'file',
  YOUTUBE: 'youtube',
  GOOGLEDOC: 'googledoc'
} as const);

export type TryMode = typeof TryModes[keyof typeof TryModes];

/**
 * Visual order of tabs in the row. Mirrors the order the plan requires:
 * Text · URL · File · YouTube · Google Doc.
 */
export const TryModeOrder: readonly TryMode[] = Object.freeze([
  TryModes.TEXT,
  TryModes.URL,
  TryModes.FILE,
  TryModes.YOUTUBE,
  TryModes.GOOGLEDOC
]) as readonly TryMode[];

/**
 * User-facing tab labels (same strings as homepage sample-card eyebrows).
 *
 * Per-preset placeholder/helper/hint copy belongs in
 * `try-mode-affordances.ts` (Phase 2) and may be tuned independently.
 */
const TryModeLabels: Readonly<Record<TryMode, string>> = Object.freeze({
  [TryModes.TEXT]: TryContentTypeLabel.TEXT,
  [TryModes.URL]: TryContentTypeLabel.URL,
  [TryModes.FILE]: TryContentTypeLabel.FILE,
  [TryModes.YOUTUBE]: TryContentTypeLabel.YOUTUBE,
  [TryModes.GOOGLEDOC]: TryContentTypeLabel.GOOGLE_DOC
});

/**
 * DOM id used for each tab button. Stable so SSR `aria-labelledby` wiring and
 * tests can address the tab without recomputing.
 */
function buildTabId(mode: TryMode): string {
  return `try-mode-tab-${mode}`;
}

export interface MountTryModeTabsOptions {
  /**
   * Empty container that becomes the tablist. Typically the SSR-rendered
   * `<div id="try-mode-tabs">` slot.
   */
  container: HTMLElement;
  /** Mode to activate on first paint (typically `'url'`). */
  defaultMode: TryMode;
  /**
   * Optional id of the tabpanel that the tabs control. When provided, the
   * panel's `aria-labelledby` is updated to point at the active tab's id.
   */
  panelId?: string;
  /**
   * Fired once on mount with `defaultMode` (`userInitiated: false`), then on
   * every active-mode change. Click + Enter/Space carry `userInitiated: true`;
   * programmatic `setMode` calls (e.g. drop-induced File switch) carry
   * `userInitiated: false` so consumers can avoid stealing focus.
   *
   * Consumers use this to apply preset affordances and to persist the mode in
   * sessionStorage. This module never persists state itself.
   */
  onChange: (mode: TryMode, opts: { userInitiated: boolean }) => void;
}

export interface TryModeTabsHandle {
  /**
   * Programmatically activate a mode (e.g. drag-drop forces File mode).
   * No-op when the requested mode is already active.
   */
  setMode(mode: TryMode): void;
  /** Currently active mode. */
  getMode(): TryMode;
  /**
   * Tear down listeners and empty the container. Safe to call on hot-reload
   * or when the flag is flipped off mid-session.
   */
  destroy(): void;
}

/**
 * Mounts the five-tab signage row into `options.container`.
 *
 * Side effects:
 * - Adds `try-mode-tabs` class and ARIA attributes to the container.
 * - Appends five `<button role="tab">` children.
 * - Wires click + keyboard listeners.
 * - Fires `onChange(defaultMode)` once before returning so consumers apply
 *   the initial preset without a separate sync call.
 */
export function mountTryModeTabs(options: MountTryModeTabsOptions): TryModeTabsHandle {
  const { container, defaultMode, panelId, onChange } = options;

  let activeMode: TryMode = defaultMode;
  const buttons = new Map<TryMode, HTMLButtonElement>();
  const cleanups: Array<() => void> = [];

  container.classList.add('try-mode-tabs');
  container.setAttribute('role', 'tablist');
  if (!container.getAttribute('aria-label')) {
    container.setAttribute('aria-label', 'Input type');
  }

  const panel = panelId ? document.getElementById(panelId) : null;

  for (const mode of TryModeOrder) {
    const button = document.createElement('button');
    button.type = 'button';
    button.id = buildTabId(mode);
    button.className = 'try-mode-tab';
    button.setAttribute('role', 'tab');

    const icon = createTryFormatIconElement(resolveTryFormatIconKind(mode), 'try-mode-tab-icon');
    const label = document.createElement('span');
    label.className = 'try-mode-tab-label';
    label.textContent = TryModeLabels[mode];
    button.appendChild(icon);
    button.appendChild(label);
    button.setAttribute('data-mode', mode);
    if (panelId) {
      button.setAttribute('aria-controls', panelId);
    }
    syncButtonState(button, mode === activeMode);

    const onClick = (): void => {
      activate(mode, { focus: false, userInitiated: true });
    };
    button.addEventListener('click', onClick);
    cleanups.push(() => button.removeEventListener('click', onClick));

    buttons.set(mode, button);
    container.appendChild(button);
  }

  const onKeydown = (event: KeyboardEvent): void => {
    handleKeydown(event);
  };
  container.addEventListener('keydown', onKeydown);
  cleanups.push(() => container.removeEventListener('keydown', onKeydown));

  // W3C ARIA tab pattern: when focus leaves the tablist (Tab key out, click
  // elsewhere, etc.) the *selected* tab must be the entry point on re-entry,
  // not whatever button arrow keys last roved focus to. Restore tabindex so
  // only the active tab carries 0 once focus has fully exited the container.
  const onFocusOut = (event: FocusEvent): void => {
    const next = event.relatedTarget as Node | null;
    if (next && container.contains(next)) return;
    for (const [m, btn] of buttons) {
      btn.tabIndex = m === activeMode ? 0 : -1;
    }
  };
  container.addEventListener('focusout', onFocusOut);
  cleanups.push(() => container.removeEventListener('focusout', onFocusOut));

  applyPanelLabelledBy(activeMode);

  // Fire initial change so consumers can apply the default preset on mount.
  onChange(activeMode, { userInitiated: false });
  scrollTabIntoView(activeMode);

  return {
    setMode(mode: TryMode): void {
      activate(mode, { focus: false, userInitiated: false });
    },
    getMode(): TryMode {
      return activeMode;
    },
    destroy(): void {
      for (const cleanup of cleanups) {
        cleanup();
      }
      cleanups.length = 0;
      while (container.firstChild) {
        container.removeChild(container.firstChild);
      }
      container.classList.remove('try-mode-tabs');
      // Reset ARIA attributes added on mount so the slot is inert after teardown.
      container.removeAttribute('role');
      container.removeAttribute('aria-label');
      if (panel) {
        panel.removeAttribute('aria-labelledby');
      }
    }
  };

  function syncButtonState(button: HTMLButtonElement, isActive: boolean): void {
    button.classList.toggle('try-mode-tab--active', isActive);
    button.setAttribute('aria-selected', String(isActive));
    button.tabIndex = isActive ? 0 : -1;
  }

  function activate(mode: TryMode, opts: { focus: boolean; userInitiated: boolean }): void {
    if (mode === activeMode) {
      if (opts.focus) {
        buttons.get(mode)?.focus();
      }
      return;
    }
    activeMode = mode;
    for (const [m, btn] of buttons) {
      syncButtonState(btn, m === mode);
    }
    applyPanelLabelledBy(mode);
    if (opts.focus) {
      buttons.get(mode)?.focus();
    }
    onChange(mode, { userInitiated: opts.userInitiated });
  }

  function applyPanelLabelledBy(mode: TryMode): void {
    if (!panel) return;
    panel.setAttribute('aria-labelledby', buildTabId(mode));
  }

  function scrollTabIntoView(mode: TryMode): void {
    buttons.get(mode)?.scrollIntoView?.({
      block: 'nearest',
      inline: 'nearest'
    });
  }

  function handleKeydown(event: KeyboardEvent): void {
    const target = event.target as HTMLElement | null;
    const targetMode = target?.getAttribute('data-mode') as TryMode | null;
    if (!targetMode || !buttons.has(targetMode)) return;

    if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
      event.preventDefault();
      activate(targetMode, { focus: false, userInitiated: true });
      return;
    }

    let nextIndex: number | null = null;
    const currentIndex = TryModeOrder.indexOf(targetMode);
    if (event.key === 'ArrowRight') {
      nextIndex = currentIndex < TryModeOrder.length - 1 ? currentIndex + 1 : 0;
    } else if (event.key === 'ArrowLeft') {
      nextIndex = currentIndex > 0 ? currentIndex - 1 : TryModeOrder.length - 1;
    } else if (event.key === 'Home') {
      nextIndex = 0;
    } else if (event.key === 'End') {
      nextIndex = TryModeOrder.length - 1;
    }

    if (nextIndex === null) return;
    event.preventDefault();
    const nextMode = TryModeOrder[nextIndex];
    const nextBtn = buttons.get(nextMode);
    if (!nextBtn) return;

    // Manual activation: arrow keys move focus only. Update tabindex so the
    // newly focused tab becomes the keyboard entry point next time.
    for (const [m, btn] of buttons) {
      btn.tabIndex = m === nextMode ? 0 : -1;
    }
    nextBtn.focus();
  }
}
